import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ViewChild, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DocumentService } from '../../../general/services/document.service';
import { SalesForm } from '../../../../features/sales/models/sales-agent.model';
import { catchError, forkJoin, map, of } from 'rxjs';
import { UserService } from '../../../user/services/user.service';
import { GlassPillDirective } from './../../../../shared/general/directives/glass-pill.directive';
import { LoaderComponent } from '../../../general/components/loader/loader.component';
import { DialogService } from '../../../services/dialog.service';
import { SalesTermsApprovalRequest } from '../../../../features/sales/models/sales-terms-form-request.model';
import { SnackBarService } from '../../../services/snackbar.service';

interface EnrichedSalesForm extends SalesForm {
  userName?: string;     // optional if some forms have no user
  presignedUrl: string;  // make it required or optional depending on your logic
}

interface UserResponse {
  firstName?: string;
  givenName?: string;
  lastName?: string;
  familyName?: string;
  displayName?: string;
  name?: string;
  fullName?: string;
  email?: string;
}

@Component({
  selector: 'app-w9-forms',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatSortModule,
    MatCardModule,
    LoaderComponent,
    GlassPillDirective
  ],
  templateUrl: './w9-forms.html',
  styleUrl: './w9-forms.scss',
})
export class W9Forms implements OnInit {
  private userService = inject(UserService);
  private documentService = inject(DocumentService);
  private dialog = inject(DialogService);
  private cdr = inject(ChangeDetectorRef);
  private snackbar = inject(SnackBarService);

  isLoading = false;
  displayedColumns: string[] = ['user', 'agent', 'lastUpdated', 'url', 'status'];
  forms: EnrichedSalesForm[] = [];
  dataSource = new MatTableDataSource<EnrichedSalesForm>();
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.dataSource.paginator = mp;
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.dataSource.sort = ms;
  }

  ngOnInit() {
    this.loadW9Forms();
  }

  loadW9Forms() {
    this.isLoading = true;
    this.cdr.detectChanges();
    
    this.documentService.getW9Forms().subscribe({
      next: (rawForms: unknown) => {
        const forms = rawForms as EnrichedSalesForm[];

        if (!forms?.length || !Array.isArray(forms)) {
          this.dataSource.data = [];
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        const uniqueUserIds = [...new Set(forms.map(f => f.userId).filter(Boolean))];

        // ── Users ────────────────────────────────────────────────────────────────
        const userRequests = uniqueUserIds.map(id =>
          this.userService.getUserById(id).pipe(
            map((user: UserResponse | null | undefined) => {
              let name = 'Unknown';

              if (user && typeof user === 'object') {
                const first = user.firstName ?? user.givenName ?? '';
                const last = user.lastName ?? user.familyName ?? '';
                const full =
                  user.displayName ??
                  user.name ??
                  user.fullName ??
                  user.email ??
                  '';

                name = full.trim() || `${first} ${last}`.trim() || id;
              }

              return { requestedId: id, name };
            }),
            catchError(() => of({ requestedId: id, name: 'Unknown' }))
          )
        );

        forkJoin([forkJoin(userRequests)]).subscribe(
          ([userResults]) => {
            // User map
            const userMap = new Map<string, string>();
            userResults.forEach(({ requestedId, name }) => {
              userMap.set(requestedId, name);
            });

            // Enrich
            const enriched = forms.map(form => ({
              ...form,
              userName: form.userId ? (userMap.get(form.userId) ?? form.userId) : '',
              //presignedUrl: presignedMap.get(form.id) || form.formUrl || ''
            }));

            this.dataSource.data = enriched;
            this.forms = enriched;
            this.isLoading = false;
            this.cdr.markForCheck();
            this.cdr.detectChanges();
          }
        );
      },
      error: err => {
        console.error('Failed to load terms forms:', err);
        this.isLoading = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    });
  }

  openForm(formId: string) {
    console.log('formid', formId);
    const form = this.forms.find(f => f.id === formId) 
            || this.dataSource.data.find(f => f.id === formId);

    if (!form?.formUrl) {
      console.warn('No formUrl found for formId:', formId);
      this.snackbar.open('No url found for this form', '', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
      return;
    }
    window.open(form.formUrl, '_blank');
  }

  approve(formId: string) {
    console.log('approve', formId);
    const selected = this.forms.filter(f => f.id === formId)[0];
    const ref = this.dialog.confirm(`Are you sure you want to approve ${selected.userName}'s W09`, 'Approve Form', 'Approve', 'Nevermind');
    ref.subscribe(result => {
      console.log('result', result);
      if (result) {
        const request: SalesTermsApprovalRequest = {
          formId: formId
        };

        this.documentService.approveW9Form(request).subscribe({
          next: () => {
            this.snackbar.open('W9 form approved', '', {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['success-snackbar']
            });
            this.loadW9Forms();
          },
          error: err => {
            console.error('Failed to approve terms form:', err);
          }
        }); 
      }
    });
  }

}
